import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtPHP, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import { MonthlyReportButton } from "@/components/admin/MonthlyReportButton";
import {
  Users,
  Banknote,
  ArrowUpRight,
  TrendingUp,
  AlertTriangle,
  Receipt,
  Clock,
  CheckCircle2,
} from "lucide-react";

function Metric({ icon: Icon, label, value, hint, tint = "emerald", testid }) {
  const tints = {
    emerald: "from-emerald-500/20 to-emerald-700/5 text-emerald-300 border-emerald-500/30",
    cyan: "from-cyan-500/20 to-cyan-700/5 text-cyan-300 border-cyan-500/30",
    amber: "from-amber-500/15 to-amber-700/5 text-amber-300 border-amber-500/30",
    rose: "from-rose-500/20 to-rose-700/5 text-rose-300 border-rose-500/40",
    purple: "from-purple-500/20 to-purple-700/5 text-purple-300 border-purple-500/30",
    slate: "from-slate-500/15 to-slate-700/5 text-slate-300 border-slate-500/30",
  };
  return (
    <Card
      data-testid={testid}
      className={`card-glow p-5 bg-gradient-to-br ${tints[tint]} border rounded-xl`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
            {label}
          </div>
          <div className="font-mono tabular text-3xl font-bold mt-2 text-white leading-tight">
            {value}
          </div>
          {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
        </div>
        <div className={`rounded-lg p-2 bg-white/5 border border-white/5`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/dashboard/stats");
      setData(data);
    })();
  }, []);

  if (!data) {
    return (
      <div className="py-20 text-center text-slate-400 font-mono" data-testid="dashboard-loading">
        Loading dashboard…
      </div>
    );
  }

  const { counts, money, recent_loans, recent_payments } = data;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
            Overview
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white mt-1">
            Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            Snapshot of borrowers, loans and collections across LendingPH.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <MonthlyReportButton />
          <div className="font-mono text-xs text-slate-500">{fmtDate(new Date().toISOString())}</div>
        </div>
      </header>

      {/* Money metrics */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          icon={Banknote}
          label="Total disbursed"
          value={fmtPHP(money.total_disbursed)}
          hint={`${counts.total_loans} loans`}
          tint="emerald"
          testid="admin-total-disbursed-metric"
        />
        <Metric
          icon={TrendingUp}
          label="Repayments collected"
          value={fmtPHP(money.total_collected)}
          hint="All-time"
          tint="cyan"
          testid="admin-total-collected-metric"
        />
        <Metric
          icon={Receipt}
          label="Outstanding principal"
          value={fmtPHP(money.total_outstanding)}
          hint={`of ${fmtPHP(money.total_repayment_scheduled)} scheduled`}
          tint="slate"
          testid="admin-outstanding-metric"
        />
        <Metric
          icon={AlertTriangle}
          label="Overdue loans"
          value={counts.overdue}
          hint="Needs attention"
          tint="rose"
          testid="admin-overdue-loans-metric"
        />
      </section>

      {/* Count metrics */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Metric
          icon={Users}
          label="Borrowers"
          value={counts.total_borrowers}
          tint="emerald"
          testid="admin-total-borrowers-card"
        />
        <Metric icon={Clock} label="Pending" value={counts.pending} tint="amber" testid="metric-pending" />
        <Metric
          icon={CheckCircle2}
          label="Approved"
          value={counts.approved}
          tint="emerald"
          testid="metric-approved"
        />
        <Metric icon={Banknote} label="Disbursed" value={counts.disbursed} tint="cyan" testid="metric-disbursed" />
        <Metric icon={TrendingUp} label="Active" value={counts.active} tint="emerald" testid="metric-active" />
        <Metric
          icon={CheckCircle2}
          label="Fully Paid"
          value={counts.fully_paid}
          tint="purple"
          testid="metric-fully-paid"
        />
      </section>

      {/* Recent activity */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-glow p-6 rounded-xl bg-[#111826] border border-white/5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-white">Recent loans</h2>
            <button
              onClick={() => navigate("/loans")}
              data-testid="dashboard-view-all-loans"
              className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
          <div className="space-y-3">
            {recent_loans.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">
                No loans yet. Create your first one.
              </div>
            )}
            {recent_loans.map((l) => (
              <button
                key={l.id}
                onClick={() => navigate(`/loans/${l.id}`)}
                data-testid={`dashboard-recent-loan-${l.id}`}
                className="w-full flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-emerald-500/30 hover:bg-white/10 transition-all text-left"
              >
                <div>
                  <div className="font-semibold text-white text-sm">{l.borrower_name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {fmtPHP(l.principal)} · {l.term_months} mo
                  </div>
                </div>
                <StatusBadge status={l.status} />
              </button>
            ))}
          </div>
        </Card>

        <Card className="card-glow p-6 rounded-xl bg-[#111826] border border-white/5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-white">Recent payments</h2>
            <button
              onClick={() => navigate("/payments")}
              data-testid="dashboard-view-all-payments"
              className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
          <div className="space-y-3">
            {recent_payments.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">
                No payments recorded yet.
              </div>
            )}
            {recent_payments.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/5"
                data-testid={`dashboard-recent-payment-${p.id}`}
              >
                <div>
                  <div className="font-semibold text-white text-sm">{p.borrower_name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {p.receipt_no} · {p.method}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono tabular text-emerald-300 font-semibold">
                    {fmtPHP(p.amount)}
                  </div>
                  <div className="text-xs text-slate-500">{fmtDate(p.paid_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
